import { useState, useEffect } from 'react';
import { apiClient } from '../lib/api-client';

interface LanguageTier {
  language_code: string;
  tier: number;
  name_english: string;
  name_native: string;
  updated_at: string;
  promoted_at: string;
  demoted_at?: string;
  notes: string;
}

interface TiersResponse {
  languages: LanguageTier[];
  tiers: Record<number, LanguageTier[]>;
  total: number;
}

interface TierStats {
  languages: number;
  dialogs: number;
}

interface StatsResponse {
  tier_stats: Record<number, TierStats>;
}

const TIER_CONFIG: Record<number, { label: string; color: string; bgColor: string; description: string; model: string }> = {
  1: { label: 'Tier 1', color: 'text-emerald-400', bgColor: 'bg-emerald-900/20 border-emerald-800', description: 'Near-ready (all levels)', model: 'Sonnet 4.6' },
  2: { label: 'Tier 2', color: 'text-blue-400', bgColor: 'bg-blue-900/20 border-blue-800', description: 'Need A2-B2 top-up', model: 'Sonnet 4.6' },
  3: { label: 'Tier 3', color: 'text-amber-400', bgColor: 'bg-amber-900/20 border-amber-800', description: 'Long tail (A1-A2 only)', model: 'Opus 4.7' },
  4: { label: 'Tier 4', color: 'text-zinc-400', bgColor: 'bg-zinc-800/50 border-zinc-700', description: 'Testing / gloss-only', model: 'Opus 4.7' },
};

export default function Languages() {
  const [data, setData] = useState<TiersResponse | null>(null);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addTier, setAddTier] = useState<number>(4);

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      setLoading(true);
      const resp = await apiClient<TiersResponse>('/api/admin/languages/tiers');
      setData(resp);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tiers');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const resp = await apiClient<StatsResponse>('/api/admin/languages/tiers/stats');
      setStats(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setStatsLoading(false);
    }
  };

  const moveTier = async (code: string, newTier: number, nameEnglish: string) => {
    try {
      await apiClient(`/api/admin/languages/tiers/${code}`, {
        method: 'PUT',
        body: JSON.stringify({ tier: newTier, name_english: nameEnglish }),
      });
      await loadTiers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tier');
    }
  };

  const deleteLanguage = async (code: string) => {
    try {
      await apiClient(`/api/admin/languages/tiers/${code}`, { method: 'DELETE' });
      await loadTiers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-white mb-4">Language Tiers</h1>
        <p className="text-zinc-500">Loading tier data...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold text-white mb-4">Language Tiers</h1>
        <p className="text-red-400">{error}</p>
        <button onClick={loadTiers} className="mt-2 px-3 py-1 bg-zinc-700 rounded text-sm text-zinc-300 hover:bg-zinc-600">
          Retry
        </button>
      </div>
    );
  }

  const tiers = data?.tiers || { 1: [], 2: [], 3: [], 4: [] };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white">Language Tiers</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Org-wide tier assignments. Determines model selection, generation scope, and priority.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadStats}
            disabled={statsLoading}
            className="px-3 py-1.5 bg-zinc-700 rounded text-sm text-zinc-300 hover:bg-zinc-600 disabled:opacity-50"
          >
            {statsLoading ? 'Loading...' : 'Load Stats'}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-3 py-1.5 bg-teal-700 rounded text-sm text-white hover:bg-teal-600"
          >
            + Add Language
          </button>
        </div>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {([1, 2, 3, 4] as const).map((t) => {
          const cfg = TIER_CONFIG[t]!;
          const tierLangs = tiers[t] || [];
          const st = stats?.tier_stats?.[t];
          return (
            <div key={t} className={`border rounded-lg p-3 ${cfg.bgColor}`}>
              <div className={`text-xs uppercase tracking-wider ${cfg.color}`}>{cfg.label}</div>
              <div className="text-xl font-semibold text-white mt-0.5">{tierLangs.length} languages</div>
              <div className="text-zinc-500 text-xs mt-1">{cfg.description}</div>
              <div className="text-zinc-500 text-xs">Model: {cfg.model}</div>
              {st && (
                <div className="text-zinc-400 text-xs mt-1 border-t border-zinc-700/50 pt-1">
                  {st.dialogs.toLocaleString()} dialogs generated
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tier columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {([1, 2, 3, 4] as const).map((t) => {
          const cfg = TIER_CONFIG[t]!;
          const tierLangs = (tiers[t] || []) as LanguageTier[];
          return (
            <div key={t} className={`border rounded-lg overflow-hidden ${cfg.bgColor}`}>
              <div className="px-3 py-2 border-b border-zinc-700/50">
                <span className={`font-medium text-sm ${cfg.color}`}>{cfg.label}</span>
                <span className="text-zinc-500 text-xs ml-2">({tierLangs.length})</span>
              </div>
              <div className="max-h-[calc(100vh-420px)] overflow-y-auto">
                {tierLangs.length === 0 ? (
                  <div className="p-3 text-zinc-600 text-xs">No languages</div>
                ) : (
                  tierLangs.map((lang) => (
                    <LanguageRow
                      key={lang.language_code}
                      lang={lang}
                      onPromote={t > 1 ? () => moveTier(lang.language_code, t - 1, lang.name_english) : undefined}
                      onDemote={t < 4 ? () => moveTier(lang.language_code, t + 1, lang.name_english) : undefined}
                      onDelete={() => deleteLanguage(lang.language_code)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showAddModal && (
        <AddLanguageModal
          defaultTier={addTier}
          onClose={() => setShowAddModal(false)}
          onSave={async (code, name, tier) => {
            await apiClient(`/api/admin/languages/tiers/${code}`, {
              method: 'PUT',
              body: JSON.stringify({ tier, name_english: name }),
            });
            setShowAddModal(false);
            await loadTiers();
          }}
        />
      )}
    </div>
  );
}

function LanguageRow({
  lang,
  onPromote,
  onDemote,
  onDelete,
}: {
  lang: LanguageTier;
  onPromote?: () => void;
  onDemote?: () => void;
  onDelete: () => void;
}) {
  const [showActions, setShowActions] = useState(false);

  return (
    <div
      className="flex items-center gap-1 px-3 py-1.5 border-t border-zinc-800/50 hover:bg-zinc-800/30 group"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="flex-1 min-w-0">
        <span className="text-zinc-200 text-sm font-mono">{lang.language_code}</span>
        <span className="text-zinc-500 text-xs ml-2 truncate">{lang.name_english}</span>
      </div>
      {showActions && (
        <div className="flex gap-1 shrink-0">
          {onPromote && (
            <button
              onClick={onPromote}
              className="px-1.5 py-0.5 text-xs bg-emerald-900/40 text-emerald-400 rounded hover:bg-emerald-900/60"
              title="Promote (move up)"
            >
              ^
            </button>
          )}
          {onDemote && (
            <button
              onClick={onDemote}
              className="px-1.5 py-0.5 text-xs bg-amber-900/40 text-amber-400 rounded hover:bg-amber-900/60"
              title="Demote (move down)"
            >
              v
            </button>
          )}
          <button
            onClick={onDelete}
            className="px-1.5 py-0.5 text-xs bg-red-900/40 text-red-400 rounded hover:bg-red-900/60"
            title="Remove"
          >
            x
          </button>
        </div>
      )}
    </div>
  );
}

function AddLanguageModal({
  defaultTier,
  onClose,
  onSave,
}: {
  defaultTier: number;
  onClose: () => void;
  onSave: (code: string, name: string, tier: number) => Promise<void>;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [tier, setTier] = useState(defaultTier);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSaving(true);
    try {
      await onSave(code.trim().toUpperCase(), name.trim() || code.trim().toUpperCase(), tier);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4 md:p-6 w-full max-w-[384px] mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-white font-medium mb-4">Add Language to Tier</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-zinc-400 text-xs block mb-1">Language Code (ISO 639-3)</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. KOR, JPN, RUS"
              className="w-full px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:border-teal-600 focus:outline-none"
              autoFocus
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs block mb-1">English Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Korean"
              className="w-full px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white placeholder-zinc-500 focus:border-teal-600 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-zinc-400 text-xs block mb-1">Tier</label>
            <select
              value={tier}
              onChange={(e) => setTier(parseInt(e.target.value, 10))}
              className="w-full px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white focus:border-teal-600 focus:outline-none"
            >
              <option value={1}>Tier 1 — Near-ready (Sonnet 4.6)</option>
              <option value={2}>Tier 2 — Need top-up (Sonnet 4.6)</option>
              <option value={3}>Tier 3 — Long tail (Opus 4.7)</option>
              <option value={4}>Tier 4 — Testing (Opus 4.7)</option>
            </select>
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={saving || !code.trim()}
              className="flex-1 px-3 py-1.5 bg-teal-700 rounded text-sm text-white hover:bg-teal-600 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Add'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 bg-zinc-700 rounded text-sm text-zinc-300 hover:bg-zinc-600"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
